// ════════════════════════════════════════════════════════════════════════════
// LPA Wealth — TanStack Query client + helper hooks
// Sprint Tech Debt #3 (audit P1-10)
//
// Substitui o useQuery artesanal de src/shared/hooks.ts.
// Adiciona:
//   • Cache global com staleTime / gcTime sensatos
//   • Dedup automático
//   • Invalidação por chave
//   • Refetch on window focus desligado por padrão (LPA é aplicação interna,
//     evita refetch quando AAI volta da reunião)
//
// Migração incremental:
//   • Componentes novos importam useLpaQuery direto
//   • Componentes legados continuam com useQuery artesanal até refactor
//   • Quando service usa Supabase, queryFn é apenas um wrapper async
// ════════════════════════════════════════════════════════════════════════════

import {
  QueryClient,
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from "@tanstack/react-query";

// ─── Stale time presets ─────────────────────────────────────────────────────
// Calibrados para LPA Wealth (dados raramente mudam mid-day):
//
//   • REALTIME (5 min)    → activity feed, alertas, notifications
//   • FREQUENT (30 min)   → snapshots, drift, KPIs do AAI
//   • STABLE   (60 min)   → suitability, target_allocations, branding
//   • REFERENCE (24h)     → benchmarks (CDI/IBOV), system_metrics
// ═══════════════════════════════════════════════════════════════════════════

export const STALE = {
  REALTIME: 5 * 60 * 1000,
  FREQUENT: 30 * 60 * 1000,
  STABLE: 60 * 60 * 1000,
  REFERENCE: 24 * 60 * 60 * 1000,
} as const;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE.FREQUENT,
      gcTime: 10 * 60 * 1000, // 10 min — entries fora-de-vista expiram rápido pra liberar memória
      retry: 1,
      refetchOnWindowFocus: false, // ver justificativa acima
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 0,
    },
  },
});

// ─── Helper hook tipado com sane defaults ───────────────────────────────────

export interface LpaQueryOptions<T> {
  staleTime?: number;
  enabled?: boolean;
  /** Override gcTime se a entry deve viver mais */
  gcTime?: number;
  /** Manter dados anteriores enquanto refetch (substituto do placeholderData) */
  keepPreviousData?: boolean;
}

/**
 * useLpaQuery — wrapper opinionated para useQuery.
 *
 * @example
 *   const { data, isLoading } = useLpaQuery(
 *     ['snapshot', clientId],
 *     () => fetchLatestSnapshot(clientId),
 *     { staleTime: STALE.FREQUENT }
 *   );
 */
export function useLpaQuery<T>(
  queryKey: ReadonlyArray<unknown>,
  queryFn: () => Promise<T>,
  opts: LpaQueryOptions<T> = {}
): UseQueryResult<T> {
  const config: UseQueryOptions<T, Error, T, ReadonlyArray<unknown>> = {
    queryKey,
    queryFn,
    staleTime: opts.staleTime ?? STALE.FREQUENT,
    gcTime: opts.gcTime,
    enabled: opts.enabled ?? true,
  };

  if (opts.keepPreviousData) {
    // v5: placeholderData mantém valor anterior durante refetch
    (config as { placeholderData?: unknown }).placeholderData = (previousData: T | undefined) =>
      previousData;
  }

  return useQuery(config);
}

/**
 * Invalidação targetizada por prefixo de queryKey.
 * Útil após mutations: ex: depois de criar trade_proposal,
 * invalidar ['trade-proposals', aaiUserId].
 */
export function invalidateLpaQuery(prefix: ReadonlyArray<unknown>): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: prefix });
}

/**
 * Setter direto no cache (útil para optimistic updates).
 */
export function setLpaQueryData<T>(key: ReadonlyArray<unknown>, data: T): void {
  queryClient.setQueryData(key, data);
}

/**
 * Pega valor cached sem disparar fetch.
 */
export function peekLpaQueryData<T>(key: ReadonlyArray<unknown>): T | undefined {
  return queryClient.getQueryData<T>(key);
}
